// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { findEndpointDevices } from '@api/portalServices';
import {
  ALL_ITEMS_PATTERN,
  type EndpointDevicesFindRequest,
  type EndpointDevicesFindResponse,
  type SortField,
} from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useInfiniteQuery } from '@tanstack/react-query';

export type EndpointsListParams = {
  searchPattern: string;
  sortField: SortField;
  pageSize: number;
};

const SEARCH_FIELDS = ['hostname'];

const buildRequest = (
  { searchPattern, sortField, pageSize }: EndpointsListParams,
  pageToken: string,
): EndpointDevicesFindRequest => {
  if (pageToken) {
    return { pageToken, pageSize };
  }
  return {
    selectors: {
      search: { pattern: searchPattern ? `*${searchPattern}*` : ALL_ITEMS_PATTERN, fields: SEARCH_FIELDS },
      sortFields: [sortField],
    },
    pageSize,
  };
};

/**
 * Server-side endpoints list query with infinite scroll (am-portal standard;
 * mirrors `useUsers`). Search/sort/paging are resolved by the BFF; pages are
 * flattened into a single device list and the next page is fetched as the
 * sentinel scrolls into view. Reads `tenantId` from the session and defers
 * until it is available so an empty tenant never reaches the BFF.
 */
export const useEndpointsList = (params: EndpointsListParams) => {
  const { tenantId } = useSession();

  const query = useInfiniteQuery<EndpointDevicesFindResponse, ApiError>({
    queryKey: [QueryKey.ENDPOINTS, params, tenantId],
    queryFn: ({ pageParam }) => findEndpointDevices(buildRequest(params, pageParam as string)),
    initialPageParam: '',
    getNextPageParam: lastPage => lastPage.pagingInfo?.afterToken || undefined,
    enabled: Boolean(tenantId),
    retry: false,
  });

  return {
    endpointDevices: query.data?.pages.flatMap(page => page.endpointDevices) ?? [],
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};
